import type { FC, ReactNode } from 'react';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { mergeRefs } from 'use-callback-ref';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Pressable, xcss } from '@atlaskit/primitives';
import VisuallyHidden from '@atlaskit/visually-hidden';
import Tooltip from '@atlaskit/tooltip';
import VideoPauseOverlayIcon from '@atlaskit/icon/core/video-pause-overlay';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

const i18n = defineMessages({
	pauseButtonLabel: {
		id: 'audio.play-pause-button.pause-label',
		defaultMessage: 'Pause',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will pause the actively playing audio',
	},
	playButtonLabel: {
		id: 'audio.play-pause-button.play-label',
		defaultMessage: 'Play',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will play the actively paused audio',
	},
	loadingButtonLabel: {
		id: 'audio.play-pause-button.loading-label',
		defaultMessage: 'Loading',
		description:
			'The label is used as a tooltip and for accessibility for the icon button when the audio is loading',
	},
});

export type PlayPauseButtonProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	isPlaying: boolean;
	isLoading: boolean;
	onClick?: () => void;
	hasFetched?: boolean;
	fetchContent?: () => void;
};

const buttonStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	width: '40px',
	height: '40px',
	borderRadius: 'border.radius.circle',
	border: '1px solid',
	borderColor: 'color.border',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

export const PlayPauseButton: FC<PlayPauseButtonProps> = ({
	audioRef,
	isPlaying,
	isLoading,
	onClick = () => {},
	hasFetched = true,
	fetchContent,
}) => {
	const { formatMessage } = useIntl();
	const pressableRef = useRef<HTMLButtonElement>(null);
	const updateTooltip = useRef<() => void>();

	useEffect(() => {
		if (pressableRef.current) {
			pressableRef.current.focus();
		}
	}, [isPlaying]);

	let labelValue: string;
	let buttonIcon: ReactNode;

	if (isLoading) {
		labelValue = formatMessage(i18n.loadingButtonLabel);
		buttonIcon = <Spinner label={labelValue} testId="miniplayer-loading" />;
	} else if (isPlaying) {
		labelValue = formatMessage(i18n.pauseButtonLabel);
		buttonIcon = (
			<VideoPauseOverlayIcon
				color={token('color.icon.selected')}
				label=""
				testId="miniplayer-pause"
			/>
		);
	} else {
		labelValue = formatMessage(i18n.playButtonLabel);
		buttonIcon = (
			<VideoPlayOverlayIcon
				color={token('color.icon.selected')}
				label=""
				testId="miniplayer-play"
			/>
		);
	}

	useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [isLoading, isPlaying]);

	const handlePause = () => {
		if (audioRef.current) {
			audioRef.current.pause();
		}
	};

	const handlePlay = async () => {
		if (audioRef.current && hasFetched) {
			await audioRef.current.play();
		} else {
			fetchContent?.();
		}
	};

	const handleClick = async () => {
		if (isPlaying) {
			handlePause();
		} else {
			await handlePlay();
		}
		onClick?.();
	};

	return (
		<Tooltip
			content={({ update }) => {
				updateTooltip.current = update;
				return labelValue;
			}}
		>
			{({ onClick, ref: tooltipRef, ...tooltipProps }) => (
				<Pressable
					xcss={buttonStyles}
					onClick={handleClick}
					componentName="PlayPauseButton"
					isDisabled={isLoading}
					ref={mergeRefs([pressableRef, tooltipRef])}
					testId="audio-play-pause-button"
					{...tooltipProps}
				>
					{buttonIcon}
					<VisuallyHidden>{labelValue}</VisuallyHidden>
				</Pressable>
			)}
		</Tooltip>
	);
};
